/**
 * SyncQueue — Queue offline writes and flush to Supabase when online.
 *
 * Architecture:
 *   1. When the user is offline, writes go to IndexedDB (Dexie) + sync_queue.
 *   2. When the browser comes back online, `flush()` is called.
 *   3. Each queued operation is replayed against Supabase in FIFO order.
 *   4. Failed operations are retried up to MAX_RETRIES times.
 *   5. A `pendingChanges` count is exposed for the UI indicator.
 *
 * Conflict Resolution Strategy:
 *   - Last-write-wins for updates (server's `updated_at` vs local `updated_at`)
 *   - Inserts are upserted to handle duplicate key scenarios
 *   - Deletes are idempotent (no error if record already removed)
 *
 * @since 1.0.0
 */
import { offlineDb, type SyncQueueEntry } from './offline';
import { supabase } from './supabase';

const MAX_RETRIES = 5;

type SyncListener = (pending: number) => void;

class SyncQueue {
    private listeners: Set<SyncListener> = new Set();
    private flushing = false;
    private flushTimer: ReturnType<typeof setTimeout> | null = null;

    // ── Public API ───────────────────────────────────────────

    /**
     * Enqueue a write operation for later sync.
     * Also writes to the local Dexie table immediately for instant UI update.
     */
    async enqueue(entry: Omit<SyncQueueEntry, 'id' | 'retries' | 'status' | 'queued_at'>): Promise<void> {
        await offlineDb.sync_queue.add({
            ...entry,
            queued_at: new Date().toISOString(),
            retries: 0,
            status: 'pending',
        });

        void this.notifyListeners();
    }

    /**
     * Enqueue an INSERT. Writes to local DB immediately.
     */
    async queueInsert(
        table: string,
        orgId: string,
        recordId: string,
        data: Record<string, unknown>,
    ): Promise<void> {
        // Write to local Dexie table for instant UI
        const localTable = this.getLocalTable(table);
        if (localTable) {
            await localTable.put({ ...data, id: recordId, org_id: orgId, _dirty: true });
        }

        await this.enqueue({
            table,
            operation: 'insert',
            record_id: recordId,
            payload: { ...data, id: recordId, org_id: orgId },
            org_id: orgId,
        });
    }

    /**
     * Enqueue an UPDATE. Writes to local DB immediately.
     */
    async queueUpdate(
        table: string,
        orgId: string,
        recordId: string,
        data: Record<string, unknown>,
    ): Promise<void> {
        const localTable = this.getLocalTable(table);
        if (localTable) {
            await localTable.update(recordId, { ...data, _dirty: true });
        }

        await this.enqueue({
            table,
            operation: 'update',
            record_id: recordId,
            payload: data,
            org_id: orgId,
        });
    }

    /**
     * Enqueue a DELETE.
     */
    async queueDelete(
        table: string,
        orgId: string,
        recordId: string,
    ): Promise<void> {
        const localTable = this.getLocalTable(table);
        if (localTable) {
            await localTable.delete(recordId);
        }

        await this.enqueue({
            table,
            operation: 'delete',
            record_id: recordId,
            payload: null,
            org_id: orgId,
        });
    }

    /**
     * Flush all pending operations to Supabase.
     * Called automatically when coming back online.
     */
    async flush(): Promise<{ synced: number; failed: number }> {
        if (this.flushing) return { synced: 0, failed: 0 };
        this.flushing = true;

        let synced = 0;
        let failed = 0;

        try {
            const pending = await offlineDb.sync_queue
                .where('status')
                .anyOf(['pending', 'failed'])
                .sortBy('queued_at');

            for (const entry of pending) {
                if (entry.retries >= MAX_RETRIES) {
                    // Mark as permanently failed
                    await offlineDb.sync_queue.update(entry.id!, {
                        status: 'failed',
                        last_error: `Max retries (${MAX_RETRIES}) exceeded`,
                    });
                    failed++;
                    continue;
                }

                // Mark as processing
                await offlineDb.sync_queue.update(entry.id!, { status: 'processing' });

                try {
                    await this.processEntry(entry);

                    // Success — remove from queue
                    await offlineDb.sync_queue.delete(entry.id!);

                    // Clear dirty flag on local record
                    const localTable = this.getLocalTable(entry.table);
                    if (localTable && entry.operation !== 'delete') {
                        await localTable.update(entry.record_id, { _dirty: false });
                    }

                    synced++;
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Unknown sync error';

                    // Exponential backoff: retry later
                    await offlineDb.sync_queue.update(entry.id!, {
                        status: 'pending',
                        retries: entry.retries + 1,
                        last_error: errorMsg,
                    });
                    failed++;
                }
            }
        } finally {
            this.flushing = false;
            this.notifyListeners();
        }

        return { synced, failed };
    }

    /**
     * Schedule a flush with debounce (avoids rapid retries).
     */
    scheduleFlush(delayMs = 1000): void {
        if (this.flushTimer) clearTimeout(this.flushTimer);
        this.flushTimer = setTimeout(() => {
            this.flush().catch(console.error);
        }, delayMs);
    }

    /**
     * Get the number of pending sync operations.
     */
    async getPendingCount(): Promise<number> {
        return offlineDb.sync_queue
            .where('status')
            .anyOf(['pending', 'processing'])
            .count();
    }

    /**
     * Get all failed operations (for admin review).
     */
    async getFailedEntries(): Promise<SyncQueueEntry[]> {
        return offlineDb.sync_queue
            .where('status')
            .equals('failed')
            .toArray();
    }

    /**
     * Retry all failed entries.
     */
    async retryFailed(): Promise<void> {
        await offlineDb.sync_queue
            .where('status')
            .equals('failed')
            .modify({ status: 'pending', retries: 0 });

        this.scheduleFlush(500);
    }

    /**
     * Clear completed/failed entries older than the given days.
     */
    async cleanup(olderThanDays = 7): Promise<number> {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - olderThanDays);
        const cutoffStr = cutoff.toISOString();

        const old = await offlineDb.sync_queue
            .where('status')
            .equals('done')
            .filter(e => e.queued_at < cutoffStr)
            .primaryKeys();

        await offlineDb.sync_queue.bulkDelete(old);
        return old.length;
    }

    // ── Listeners ──────────────────────────────────────────

    subscribe(listener: SyncListener): () => void {
        this.listeners.add(listener);
        // Immediately notify with current count
        this.getPendingCount().then(count => listener(count));
        return () => this.listeners.delete(listener);
    }

    private async notifyListeners(): Promise<void> {
        const count = await this.getPendingCount();
        this.listeners.forEach(fn => fn(count));
    }

    // ── Internals ──────────────────────────────────────────

    /**
     * Process a single sync queue entry against Supabase.
     */
    private async processEntry(entry: SyncQueueEntry): Promise<void> {
        const { table, operation, record_id, payload } = entry;

        switch (operation) {
            case 'insert': {
                if (!payload) throw new Error('Insert requires payload');
                // Upsert to handle conflicts (record may already exist on server)
                const { error } = await supabase
                    .from(table)
                    .upsert(payload, { onConflict: 'id' });
                if (error) throw new Error(`Insert failed: ${error.message}`);
                break;
            }

            case 'update': {
                if (!payload) throw new Error('Update requires payload');
                const { error } = await supabase
                    .from(table)
                    .update(payload)
                    .eq('id', record_id);
                if (error) throw new Error(`Update failed: ${error.message}`);
                break;
            }

            case 'delete': {
                const { error } = await supabase
                    .from(table)
                    .delete()
                    .eq('id', record_id);
                // Ignore "not found" errors for deletes (idempotent)
                if (error && !error.message.includes('not found')) {
                    throw new Error(`Delete failed: ${error.message}`);
                }
                break;
            }

            default:
                throw new Error(`Unknown operation: ${operation}`);
        }
    }

    /**
     * Map Supabase table name to local Dexie table.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getLocalTable(table: string): any {
        const map: Record<string, unknown> = {
            transactions: offlineDb.transactions,
            journal_entries: offlineDb.journal_entries,
            contacts: offlineDb.contacts,
            items: offlineDb.items,
            accounts: offlineDb.accounts,
        };
        return map[table] ?? null;
    }
}

// Singleton
export const syncQueue = new SyncQueue();
